import React from 'react';
import { BLUE_PRIMARY, BLUE_SOFT_SURFACE, BLUE_PRIMARY_DARK } from '../theme';

export const Chip = ({ label, active }) => (
  <span style={{
    padding: '6px 10px',
    borderRadius: 999,
    border: '1px solid ' + (active ? BLUE_PRIMARY : '#e5e7eb'),
    color: active ? BLUE_PRIMARY_DARK : '#374151',
    fontSize: 12,
    background: active ? BLUE_SOFT_SURFACE : '#fff',
    whiteSpace: 'nowrap',
    lineHeight: 1.1,
  }}>{label}</span>
);

export const useChartWidth = (deduction = 48, maxWidth = 720) => {
  const [width, setWidth] = React.useState(Math.min(window.innerWidth, maxWidth) - deduction);

  React.useEffect(() => {
    const handleResize = () => {
      setWidth(Math.min(window.innerWidth, maxWidth) - deduction);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [deduction, maxWidth]);

  return width;
};

export const MiniLine = ({ values, dates, showAvg, avg, showDots = false, width, color }) => {
  const w = width || 300;
  const h = 100; // mobile-friendly chart with tiny axes
  const c = color || BLUE_PRIMARY;
  if (!values || !values.length) return <div style={{ height: h }} />;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const padLeft = 32; // space for y labels
  const pad = 8;
  const coords = values.map((v, i) => {
    const x = padLeft + (i * (w - padLeft - pad)) / Math.max(1, values.length - 1);
    const nv = (max === min) ? 0.5 : (v - min) / (max - min);
    const y = h - pad - nv * (h - pad * 2);
    return { x, y };
  });
  const pts = coords.map(c => `${c.x},${c.y}`).join(' ');
  const avgY = (avg != null && max !== min)
    ? (h - pad - ((avg - min) / (max - min)) * (h - pad * 2))
    : (h / 2);

  // x-axis labels: first, mid, last
  const xFirst = padLeft;
  const xLast = w - pad;
  const xMid = (xFirst + xLast) / 2;
  const firstLabel = dates && dates.length ? new Date(dates[0]) : null;
  const lastLabel = dates && dates.length ? new Date(dates[dates.length - 1]) : null;
  const midLabel = dates && dates.length >= 3 ? new Date(dates[Math.floor(dates.length / 2)]) : null;
  const fmt = (d) => d ? d.toLocaleDateString('tr-TR', { month: '2-digit', day: '2-digit' }) : '';
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" width="100%" height={h} style={{ display: 'block', width: '100%', maxWidth: '100%' }}>
      {/* Y min/max labels */}
      <text x={2} y={12} fontSize="10" fill="#64748b">{max.toFixed(0)}</text>
      <text x={2} y={h - 2} fontSize="10" fill="#64748b">{min.toFixed(0)}</text>
      {/* Average line */}
      {showAvg && avg != null && (
        <g>
          <line x1={padLeft} y1={avgY} x2={w - pad} y2={avgY} stroke="#94a3b8" strokeDasharray="4,4" strokeWidth="1" />
          <text x={w - pad - 36} y={avgY - 4} fontSize="10" fill="#475569">Ort.</text>
        </g>
      )}
      {/* Data polyline */}
      <polyline fill="none" stroke={c} strokeWidth="3" points={pts} />
      {/* Data points */}
      {showDots && coords.map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r={2.5} fill={c} stroke="#fff" strokeWidth="1" />
      ))}
      {/* X-axis labels */}
      <text x={xFirst} y={h} fontSize="10" fill="#64748b">{fmt(firstLabel)}</text>
      {midLabel && <text x={xMid - 16} y={h} fontSize="10" fill="#64748b">{fmt(midLabel)}</text>}
      <text x={xLast - 28} y={h} fontSize="10" fill="#64748b">{fmt(lastLabel)}</text>
    </svg>
  );
};

export const MiniArea = ({ values, dates, showAvg, avg, width, color }) => {
  const w = width || 300;
  const h = 100; // same canvas as MiniLine
  const c = color || BLUE_PRIMARY;
  if (!values || !values.length) return <div role="status" style={{ height: h, display: 'flex', alignItems: 'center', color: '#64748b', fontSize: 12 }}>Bu dönemde çalışma kaydı yok.</div>;
  const single = values.length === 1;
  let max = Math.max(...values);
  let min = Math.min(...values);
  const padLeft = 32;
  const pad = 8;
  // Avoid zero range rendering
  if (max === min) {
    if (max === 0) { max = 1; min = 0; }
    else { max = max + 1; min = min - 1; }
  }
  const coords = values.map((v, i) => {
    const x = padLeft + (single ? (w - padLeft - pad) / 2 : (i * (w - padLeft - pad)) / (values.length - 1));
    const nv = (v - min) / (max - min);
    const y = h - pad - nv * (h - pad * 2);
    return { x, y };
  });
  const linePts = coords.map(c => `${c.x},${c.y}`).join(' ');
  const baseY = h - pad;
  const areaPts = `${padLeft},${baseY} ${linePts} ${w - pad},${baseY}`;
  const avgY = (avg != null)
    ? (h - pad - ((avg - min) / (max - min)) * (h - pad * 2))
    : (h / 2);
  // Labels
  const xFirst = padLeft;
  const xLast = w - pad;
  const xMid = (xFirst + xLast) / 2;
  const firstLabel = dates && dates.length ? new Date(dates[0]) : null;
  const lastLabel = dates && dates.length ? new Date(dates[dates.length - 1]) : null;
  const midLabel = dates && dates.length >= 3 ? new Date(dates[Math.floor(dates.length / 2)]) : null;
  const fmt = (d) => d ? d.toLocaleDateString('tr-TR', { month: '2-digit', day: '2-digit' }) : '';
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" width="100%" height={h} style={{ display: 'block', width: '100%', maxWidth: '100%' }}>
      {/* Y min/max labels */}
      <text x={2} y={12} fontSize="10" fill="#64748b">{max.toFixed(0)}</text>
      <text x={2} y={h - 2} fontSize="10" fill="#64748b">{min.toFixed(0)}</text>
      {/* Average line */}
      {!single && showAvg && avg != null && (
        <g>
          <line x1={padLeft} y1={avgY} x2={w - pad} y2={avgY} stroke="#94a3b8" strokeDasharray="4,4" strokeWidth="1" />
          <text x={w - pad - 36} y={avgY - 4} fontSize="10" fill="#475569">Ort.</text>
        </g>
      )}
      {/* Area fill */}
      {!single && <polygon points={areaPts} fill={c} opacity="0.15" />}
      {/* Line stroke on top */}
      {!single && <polyline fill="none" stroke={c} strokeWidth="3" points={linePts} />}
      {single && <circle cx={coords[0].x} cy={coords[0].y} r={3} fill={c}><title>{`${dates?.[0] ? `${fmt(firstLabel)}: ` : ''}${values[0]}`}</title></circle>}
      {/* X-axis labels */}
      <text x={single ? xMid : xFirst} textAnchor={single ? 'middle' : undefined} y={h} fontSize="10" fill="#64748b">{fmt(firstLabel)}</text>
      {midLabel && <text x={xMid - 16} y={h} fontSize="10" fill="#64748b">{fmt(midLabel)}</text>}
      {!single && <text x={xLast - 28} y={h} fontSize="10" fill="#64748b">{fmt(lastLabel)}</text>}
    </svg>
  );
};
